import React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '../../../styles/theme';

// Tipos para las variantes personalizadas
interface CustomButtonProps {
  variant?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'info'
    | 'outline'
    | 'ghost';
  size?: 'small' | 'medium' | 'large';
}

export type ButtonProps = MuiButtonProps & CustomButtonProps;

// Styled component con estilos unificados
const StyledButton = styled(MuiButton)<CustomButtonProps>(({ theme, variant, size }) => ({
  // Estilos base
  fontFamily: theme.typography.button.fontFamily,
  fontWeight: theme.typography.button.fontWeight,
  fontSize: theme.typography.button.fontSize,
  textTransform: 'none',
  borderRadius: '8px',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',

  // Tamaños
  ...(size === 'small' && {
    padding: '6px 12px',
    minHeight: '32px',
    fontSize: '12px',
  }),
  ...(size === 'medium' && {
    padding: '8px 16px',
    minHeight: '40px',
    fontSize: '14px',
  }),
  ...(size === 'large' && {
    padding: '12px 24px',
    minHeight: '48px',
    fontSize: '16px',
  }),

  // Variantes de color
  ...(variant === 'primary' && {
    backgroundColor: colors.primary.main,
    color: colors.primary.contrastText,
    '&:hover': {
      backgroundColor: colors.primary.dark,
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    },
  }),

  ...(variant === 'secondary' && {
    backgroundColor: colors.secondary.main,
    color: colors.secondary.contrastText,
    '&:hover': {
      backgroundColor: colors.secondary.dark,
      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    },
  }),

  ...(variant === 'success' && {
    backgroundColor: colors.success.main,
    color: colors.success.contrastText,
    '&:hover': {
      backgroundColor: colors.success.dark,
      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
    },
  }),

  ...(variant === 'warning' && {
    backgroundColor: colors.warning.main,
    color: colors.warning.contrastText,
    '&:hover': {
      backgroundColor: colors.warning.dark,
      boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
    },
  }),

  ...(variant === 'error' && {
    backgroundColor: colors.error.main,
    color: colors.error.contrastText,
    '&:hover': {
      backgroundColor: colors.error.dark,
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
    },
  }),

  ...(variant === 'info' && {
    backgroundColor: colors.info.main,
    color: colors.info.contrastText,
    '&:hover': {
      backgroundColor: colors.info.dark,
      boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
    },
  }),

  ...(variant === 'outline' && {
    backgroundColor: 'transparent',
    color: colors.primary.main,
    border: `2px solid ${colors.primary.main}`,
    '&:hover': {
      backgroundColor: colors.primary.main,
      color: colors.primary.contrastText,
      boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
    },
  }),

  ...(variant === 'ghost' && {
    backgroundColor: 'transparent',
    color: colors.primary.main,
    '&:hover': {
      backgroundColor: colors.primary.light,
      color: colors.primary.contrastText,
    },
  }),
}));

// Componente Button unificado
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  children,
  ...props
}) => {
  // Mapear nuestras variantes personalizadas a las de MUI
  const muiVariant = variant === 'outline' || variant === 'ghost' ? 'outlined' : 'contained';

  return (
    <StyledButton variant={muiVariant as any} size={size} {...props}>
      {children}
    </StyledButton>
  );
};

export default Button;
